import React, { useState, useEffect, useMemo, useCallback } from 'react';
import LoadingAnimation from './LoadingAnimation';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Lock, Play, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DailyCheckIn from './DailyCheckIn';
import AcaciaTree from './AcaciaTree';
import CircleView from './CircleView';
import SimbyAnimation from './SimbyAnimation';
import { Progress } from './ui/progress';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ──────────────────────────────────────────────────────────────────────────
// Dashboard — duas abas: Início (trilha Duolingo) + Círculo.
// A antiga aba "Meu Progresso" (Diagnóstico/Mapa/Trilha/Habilidades) migrou
// para a rota /progress (ProgressDashboard) — ver briefing seção 8.1.
// ──────────────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [user, setUser] = useState(authUser);
  const [islands, setIslands] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState('inicio');

  // Gamification
  const [pots, setPots] = useState([]);
  const [activePot, setActivePot] = useState(null);
  const [resources, setResources] = useState(null);
  const [celebrationPotId, setCelebrationPotId] = useState(null);

  // Ecos recebidos (últimos 7d)
  const [ecosCount, setEcosCount] = useState(0);

  // Insights do Diário de Emoções
  const [latestInsight, setLatestInsight] = useState(null);
  const [checkinDone, setCheckinDone] = useState(false);

  useEffect(() => {
    if (authUser) setUser(authUser);
  }, [authUser]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoadError(false);
    try {
      const [islandsRes, lessonsRes, potsRes, resRes, ecosRes] = await Promise.all([
        axios.get(`${API}/islands`, { withCredentials: true }).catch(err => { throw Object.assign(err, { _source: 'islands' }); }),
        axios.get(`${API}/lessons`, { withCredentials: true }).catch(err => { throw Object.assign(err, { _source: 'lessons' }); }),
        axios.get(`${API}/gamification/my-pots`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/gamification/my-resources`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/eco/my-ecos?days=7`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
      ]);

      setIslands(islandsRes.data || []);
      setLessons(lessonsRes.data || []);

      // Fetch latest emotion diary insight
      axios.get(`${API}/emotion-checkin/insights?limit=1`, { withCredentials: true })
        .then(res => {
          const insights = res.data?.insights || [];
          if (insights.length > 0) setLatestInsight(insights[0]);
        })
        .catch(() => {});

      const potsList = potsRes.data || [];
      setPots(potsList); // BUG FIX (Simby-3): nunca era setado — contador de acácias florescidas ficava sempre em 0
      if (potsList.length > 0) {
        potsList.sort((a, b) => b.island_id - a.island_id);
        const current = potsList.find(p => !p.completed_at) || potsList[0];
        setActivePot(current);
        if (current?.simby_celebration) {
          setCelebrationPotId(current.pot_id);
        }
      }
      setResources(resRes.data);
      setEcosCount(ecosRes.data?.count || ecosRes.data?.length || 0);
    } catch (err) {
      console.error('Dashboard fetchData error:', err);
      const status = err?.response?.status;
      setLoadError(status || true);
    } finally {
      setIsLoading(false);
    }
  };

  const isLessonCompleted = useCallback((lessonId) =>
    user?.completedLessons?.includes(lessonId) ?? false,
  [user]);

  const getLessonsForIsland = useCallback((islandId) =>
    lessons
      .filter(l => l.island_id === islandId)
      .sort((a, b) => a.order - b.order)
      .map(lesson => ({
        id: lesson.lesson_id,
        title: lesson.title,
        type: lesson.type,
        xp: lesson.xp,
        completed: isLessonCompleted(lesson.lesson_id),
      })),
  [lessons, isLessonCompleted]);

  const getIslandProgress = useCallback((island) => {
    if (!user?.completedLessons) return 0;
    const islandLessons = getLessonsForIsland(island.island_id);
    if (!islandLessons.length) return 0;
    const done = islandLessons.filter(l => user.completedLessons.includes(l.id)).length;
    return Math.round((done / islandLessons.length) * 100);
  }, [user, getLessonsForIsland]);

  const isIslandLocked = useCallback((island, index) => {
    if (index === 0 || !island.requires_previous_completion) return false;
    return getIslandProgress(islands[index - 1]) < 100;
  }, [islands, getIslandProgress]);

  const islandsWithProgress = useMemo(() =>
    islands.map((island, index) => ({
      ...island,
      id: island.island_id,
      locked: isIslandLocked(island, index),
      progress: getIslandProgress(island),
      lessons: getLessonsForIsland(island.island_id),
    })),
  [islands, isIslandLocked, getIslandProgress, getLessonsForIsland]);

  const nextLessonId = useMemo(() => {
    for (const island of islandsWithProgress) {
      const next = island.lessons.find(l => !l.completed);
      if (next) return next.id;
    }
    return null;
  }, [islandsWithProgress]);

  const STAGE_THRESHOLDS = [0, 5, 15, 35, 60];
  const stage = activePot?.current_stage ?? 0;
  const pts   = activePot?.island_points ?? 0;
  const stageProgress = stage < 4
    ? Math.min(100, Math.round((pts - STAGE_THRESHOLDS[stage]) / (STAGE_THRESHOLDS[stage + 1] - STAGE_THRESHOLDS[stage]) * 100))
    : 100;

  const handleLessonClick = (lessonId, islandId, locked) => {
    if (locked) return;
    navigate(`/lesson-modules/${lessonId}`);
  };

  // Acácias florescidas (stage === 4)
  const acaciasFlorescidas = pots.filter(p => p.current_stage === 4).length;

  if (!user || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <LoadingAnimation size="md" className="mx-auto mb-4" />
          <p className="text-gray-600">Carregando…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    const is401 = loadError === 401;
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">🌵</div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Não conseguimos carregar a trilha</h2>
          <p className="text-sm text-gray-500 mb-6">
            {is401
              ? 'Sua sessão expirou. Faça login novamente para continuar.'
              : 'Verifique sua conexão e tente novamente. Se o problema persistir, recarregue a página.'}
          </p>
          {is401 ? (
            <button
              onClick={() => window.location.href = '/login'}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
            >
              Fazer login
            </button>
          ) : (
            <button
              onClick={() => { setIsLoading(true); fetchData(); }}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
            >
              Tentar novamente
            </button>
          )}
        </div>
      </div>
    );
  }

  // Gamificação fora de foco no MVP: aba "Círculo" oculta (código preservado).
  const TABS = [
    { id: 'inicio',  label: 'Início' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Welcome header */}
        <div className="mb-6 flex items-center gap-4">
          <img src="/images/simby-mascote.png" alt="Simby" className="w-12 h-16 object-contain flex-shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Olá, {user?.name?.split(' ')[0] || 'Estudante'}!
            </h1>
            <p className="text-gray-500 text-sm">Continue sua jornada de Inteligência Relacional</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-purple-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── ABA: INÍCIO ──────────────────────────────────────────────────── */}
        {activeTab === 'inicio' && (
          <div className="space-y-6">

            {/* Gamificação (métricas de acácias/ecos e card "Sua Acácia") oculta no
                MVP — fora de foco. Código preservado no histórico. Mantemos o
                check-in diário, que é parte do diário emocional, não gamificação. */}

            {/* Check-in diário */}
            <Card className="p-0 bg-gradient-to-br from-white to-purple-50 border-2 border-purple-200 overflow-hidden">
              <DailyCheckIn
                compact={true}
                currentLessonId={nextLessonId}
                postCheckinInsight={latestInsight}
                onComplete={() => {
                  setCheckinDone(true);
                  // Refresh insight after check-in
                  axios.get(`${API}/emotion-checkin/insights?limit=1`, { withCredentials: true })
                    .then(res => {
                      const insights = res.data?.insights || [];
                      if (insights.length > 0) setLatestInsight(insights[0]);
                    })
                    .catch(() => {});
                }}
              />
            </Card>

            {/* Card Perfil Relacional — visível após assessment concluído */}
            {user?.hasCompletedInitialAssessment && (
              <button
                onClick={() => navigate('/pdi')}
                className="w-full text-left"
              >
                <Card className="p-4 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-white hover:border-purple-400 hover:shadow-md transition-all flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🧭</span>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Seu Perfil Relacional</p>
                      <p className="text-xs text-gray-500">Ver seu Plano: competências, avaliações e recomendações</p>
                    </div>
                  </div>
                  <span className="text-purple-400 text-lg flex-shrink-0">›</span>
                </Card>
              </button>
            )}

            {/* Trilha de Aprendizado — cards verticais */}
            <div className="space-y-12">
              {islandsWithProgress.length === 0 ? (
                <Card className="p-8 text-center">
                  <p className="text-gray-600 mb-4">Nenhuma ilha disponível ainda.</p>
                  <p className="text-sm text-gray-500">O conteúdo será adicionado em breve!</p>
                </Card>
              ) : (
                islandsWithProgress.map((island, islandIndex) => (
                  <div key={island.id} className="relative">
                    {islandIndex < islandsWithProgress.length - 1 && (
                      <div className="absolute left-1/2 top-full h-12 w-1 bg-gradient-to-b from-purple-300 to-purple-200 transform -translate-x-1/2 z-0" />
                    )}
                    <Card
                      className={`p-6 relative overflow-hidden transition-all duration-300 ${
                        island.locked
                          ? 'opacity-60 cursor-not-allowed border-gray-300'
                          : 'hover:shadow-xl border-2 hover:border-purple-300 cursor-pointer transform hover:scale-[1.02]'
                      }`}
                      style={{
                        background: island.locked
                          ? 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
                          : `linear-gradient(135deg, ${island.color}20 0%, ${island.color}10 100%)`
                      }}
                    >
                      {/* Header da ilha */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3">
                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:space-x-4 text-center sm:text-left w-full">
                          <div
                            className="text-5xl p-4 rounded-2xl shadow-lg flex-shrink-0"
                            style={{
                              background: island.locked ? '#d1d5db' : island.color,
                              transform: island.locked ? 'none' : 'rotate(-5deg)'
                            }}
                          >
                            {island.locked ? <Lock className="w-12 h-12 text-white" /> : <span>{island.icon}</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1">
                              <div>
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{island.name}</h2>
                                <p className="text-sm sm:text-base text-gray-600">{island.description}</p>
                              </div>
                              {!island.locked && (
                                <div className="text-center sm:text-right flex-shrink-0">
                                  <div className="text-2xl sm:text-3xl font-bold" style={{ color: island.color }}>{island.progress}%</div>
                                  <div className="text-sm text-gray-600">Completo</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Barra de progresso */}
                      {!island.locked && (
                        <div className="mb-6">
                          <Progress value={island.progress} className="h-2" />
                        </div>
                      )}

                      {/* Lições — estilo Duolingo */}
                      <div className="space-y-0">
                        {island.lessons && island.lessons.length > 0 ? (
                          island.lessons.map((lesson, lessonIndex) => {
                            const isLessonLocked = island.locked || (
                              (island.sequential_lessons || island.sequentialLessons) &&
                              lessonIndex > 0 &&
                              !island.lessons[lessonIndex - 1].completed
                            );
                            const isLast = lessonIndex === island.lessons.length - 1;
                            return (
                              <div key={lesson.id} className="flex flex-col items-center w-full">
                                {/* Conector acima (exceto primeiro) */}
                                {lessonIndex > 0 && (
                                  <div className="w-0.5 h-4" style={{
                                    background: island.lessons[lessonIndex - 1].completed ? '#22c55e' : '#d1d5db'
                                  }} />
                                )}
                                {/* Ícone centralizado */}
                                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                                  lesson.completed
                                    ? 'bg-green-500 border-green-500'
                                    : isLessonLocked
                                      ? 'bg-gray-200 border-gray-300'
                                      : 'bg-white border-purple-400'
                                }`}>
                                  {lesson.completed ? (
                                    <CheckCircle2 className="w-5 h-5 text-white" />
                                  ) : isLessonLocked ? (
                                    <Lock className="w-4 h-4 text-gray-400" />
                                  ) : (
                                    <Play className="w-4 h-4 text-purple-600" />
                                  )}
                                </div>
                                {/* Conector abaixo */}
                                {!isLast && (
                                  <div className="w-0.5 h-4" style={{
                                    background: lesson.completed ? '#22c55e' : '#d1d5db'
                                  }} />
                                )}
                                {/* Card da lição — full width */}
                                <button
                                  onClick={() => handleLessonClick(lesson.id, island.id, isLessonLocked)}
                                  disabled={isLessonLocked}
                                  className={`w-full p-4 rounded-xl text-left transition-all duration-300 mt-1 mb-1 ${
                                    isLessonLocked
                                      ? 'bg-gray-100 cursor-not-allowed'
                                      : lesson.completed
                                        ? 'bg-white border-2 border-green-300 hover:border-green-400'
                                        : 'bg-white border-2 border-gray-200 hover:border-purple-400 hover:shadow-md'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-0.5">Lição</span>
                                      <h4 className="font-semibold text-gray-900 leading-tight">{lesson.title}</h4>
                                    </div>
                                    <div className="ml-3 flex-shrink-0 flex flex-col items-end gap-1">
                                      {lesson.completed && (
                                        <span className="text-green-600 text-xs font-semibold">✔ Concluído</span>
                                      )}
                                      {isLessonLocked && !island.locked && (
                                        <span className="text-gray-400 text-xs font-semibold flex items-center gap-1">
                                          <Lock className="w-3 h-3" /> Bloqueado
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-4 text-gray-500">Lições em breve...</div>
                        )}
                      </div>
                    </Card>
                  </div>
                ))
              )}
            </div>

            {/* Mascote motivacional */}
            <Card className="mt-4 p-6 bg-gradient-to-r from-purple-100 to-purple-50 border-2 border-purple-200">
              <div className="flex items-center space-x-6">
                <img src="/images/simby-mascote.png" alt="Simby" className="w-20 h-28 object-contain" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Continue assim!</h3>
                  <p className="text-gray-600 text-sm">Continue praticando para regar sua acácia e avançar na trilha 🌿</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* ── ABA: CÍRCULO ─────────────────────────────────────────────────── */}
        {activeTab === 'circulo' && (
          <CircleView />
        )}
      </div>

      {celebrationPotId && (
        <SimbyAnimation potId={celebrationPotId} onDone={() => setCelebrationPotId(null)} />
      )}
    </div>
  );
};

export default Dashboard;
